import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Kampo() {
  const [activeCategory, setActiveCategory] = useState('constitution');

  const categories = [
    { id: 'all', name: 'すべて', icon: 'ri-medicine-bottle-line' },
    { id: 'constitution', name: 'あなたの体質', icon: 'ri-user-heart-line' },
    { id: 'symptoms', name: '症状別', icon: 'ri-stethoscope-line' },
    { id: 'season', name: '季節の漢方', icon: 'ri-sun-line' },
    { id: 'women', name: '女性の悩み', icon: 'ri-women-line' },
    { id: 'elderly', name: 'シニア向け', icon: 'ri-user-line' },
  ];

  const constitutionContent = {
    title: 'あなたの体質',
    description: '東洋医学の考え方に基づいた体質診断で、あなたに最適な漢方薬を考えます。',
    types: [
      {
        name: '気虚体質',
        description: 'エネルギー不足で疲れやすい体質',
        symptoms: ['疲労感', '食欲不振', '息切れ', '風邪をひきやすい'],
        recommendations: ['補中益気湯', '十全大補湯', '人参養栄湯'],
        color: 'bg-blue-50 text-blue-600'
      },
      {
        name: '血虚体質',
        description: '血液の栄養が不足している体質',
        symptoms: ['めまい', '立ちくらみ', '肌の乾燥', '不眠'],
        recommendations: ['当帰芍薬散', '四物湯', '婦人宝'],
        color: 'bg-red-50 text-red-600'
      },
      {
        name: '陰虚体質',
        description: '体の潤いが不足している体質',
        symptoms: ['のぼせ', '手足のほてり', '口の渇き', '寝汗'],
        recommendations: ['知柏地黄丸', '麦門冬湯', '滋陰降火湯'],
        color: 'bg-orange-50 text-orange-600'
      },
      {
        name: '陽虚体質',
        description: '体を温める力が不足している体質',
        symptoms: ['冷え性', '下痢しやすい', 'むくみ', '頻尿'],
        recommendations: ['真武湯', '附子理中湯', '八味地黄丸'],
        color: 'bg-purple-50 text-purple-600'
      }
    ]
  };

  const symptomsContent = {
    title: '症状別漢方薬',
    description: '具体的な症状に応じた漢方薬の選択をサポートします',
    categories: [
      {
        name: '風邪・感冒',
        medicines: ['葛根湯', '麻黄湯', '桂枝湯', '小青竜湯'],
        description: '初期症状から回復期まで段階に応じて選択',
        icon: 'ri-thermometer-line'
      },
      {
        name: '胃腸の不調',
        medicines: ['六君子湯', '半夏瀉心湯', '安中散', '大建中湯'],
        description: '消化不良、胃痛、下痢などの症状に対応',
        icon: 'ri-capsule-line'
      },
      {
        name: '女性特有の症状',
        medicines: ['当帰芍薬散', '加味逍遙散', '桂枝茯苓丸', '温経湯'],
        description: '月経不順、更年期障害、冷え性など',
        icon: 'ri-heart-line'
      },
      {
        name: 'ストレス・不眠',
        medicines: ['甘麦大棗湯', '抑肝散', '柴胡桂枝乾姜湯', '酸棗仁湯'],
        description: '精神的な不調や睡眠の問題に対応',
        icon: 'ri-brain-line'
      }
    ]
  };

  const herbsContent = {
    title: '主要な生薬について',
    description: '漢方薬を構成する代表的な生薬の効能と特徴をご紹介します',
    herbs: [
      {
        name: '人参（ニンジン）',
        effect: '気を補い、体力を回復させる',
        uses: ['疲労回復', '食欲不振', '下痢'],
        image: 'https://public.readdy.ai/ai/img_res/3db074b2ac89e4307b65c1aa8e821805.jpg'
      },
      {
        name: '当帰（トウキ）',
        effect: '血を補い、血行を改善する',
        uses: ['貧血', '冷え性', '月経不順'],
        image: 'https://readdy.ai/api/search-image?query=Angelica%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20medicinal%20plant%20for%20blood%20circulation&width=300&height=200&seq=angelica-herb&orientation=landscape'
      },
      {
        name: '甘草（カンゾウ）',
        effect: '諸薬を調和し、解毒作用がある',
        uses: ['咳止め', '胃痛緩和', '薬の調和'],
        image: 'https://readdy.ai/api/search-image?query=Licorice%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20medicinal%20plant%20with%20sweet%20taste&width=300&height=200&seq=licorice-herb&orientation=landscape'
      },
      {
        name: '生姜（ショウキョウ）',
        effect: '体を温め、胃腸の働きを助ける',
        uses: ['冷え性', '吐き気', '食欲不振'],
        image: 'https://readdy.ai/api/search-image?query=Dried%20ginger%20traditional%20Chinese%20medicine%20herb%2C%20medicinal%20spice%20for%20warming%20body&width=300&height=200&seq=ginger-herb&orientation=landscape'
      },
      {
        name: '黄芩（オウゴン）',
        effect: '熱を清し、炎症を抑える',
        uses: ['発熱', '下痢', '炎症'],
        image: 'https://readdy.ai/api/search-image?query=Scutellaria%20baicalensis%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20yellow%20medicinal%20plant&width=300&height=200&seq=scutellaria-herb&orientation=landscape'
      },
      {
        name: '大棗（タイソウ）',
        effect: '脾胃を補い、気血を調和する',
        uses: ['消化不良', '不安', '不眠'],
        image: 'https://readdy.ai/api/search-image?query=Chinese%20jujube%20dates%20traditional%20medicine%20herb%2C%20dried%20red%20dates%20for%20digestive%20health&width=300&height=200&seq=jujube-herb&orientation=landscape'
      },
      {
        name: '白朮（ビャクジュツ）',
        effect: '脾胃を健やかにし、水湿を除く',
        uses: ['消化不良', 'むくみ', '下痢'],
        image: 'https://readdy.ai/api/search-image?query=Atractylodes%20macrocephala%20rhizome%20traditional%20Chinese%20medicine%20herb%2C%20dried%20white%20medicinal%20root&width=300&height=200&seq=atractylodes-herb&orientation=landscape'
      },
      {
        name: '茯苓（ブクリョウ）',
        effect: '利水滲湿、健脾安神の効果',
        uses: ['むくみ', '不眠', '動悸'],
        image: 'https://readdy.ai/api/search-image?query=Poria%20cocos%20mushroom%20traditional%20Chinese%20medicine%20herb%2C%20dried%20white%20fungus%20for%20water%20metabolism&width=300&height=200&seq=poria-herb&orientation=landscape'
      },
      {
        name: '芍薬（シャクヤク）',
        effect: '血を養い、肝を柔らかくする',
        uses: ['月経不順', '腹痛', '筋肉痛'],
        image: 'https://readdy.ai/api/search-image?query=Peony%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20white%20medicinal%20root%20for%20blood%20nourishment&width=300&height=200&seq=peony-herb&orientation=landscape'
      },
      {
        name: '川芎（センキュウ）',
        effect: '血行を促進し、気を巡らせる',
        uses: ['頭痛', '月経痛', '血行不良'],
        image: 'https://readdy.ai/api/search-image?query=Ligusticum%20chuanxiong%20rhizome%20traditional%20Chinese%20medicine%20herb%2C%20dried%20medicinal%20root%20for%20blood%20circulation&width=300&height=200&seq=ligusticum-herb&orientation=landscape'
      },
      {
        name: '地黄（ジオウ）',
        effect: '腎陰を補い、血を養う',
        uses: ['腎虚', '貧血', '糖尿病'],
        image: 'https://readdy.ai/api/search-image?query=Rehmannia%20glutinosa%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20black%20medicinal%20root%20for%20kidney%20health&width=300&height=200&seq=rehmannia-herb&orientation=landscape'
      },
      {
        name: '黄耆（オウギ）',
        effect: '気を補い、表を固める',
        uses: ['免疫力向上', '疲労', '汗かき'],
        image: 'https://readdy.ai/api/search-image?query=Astragalus%20membranaceus%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20yellow%20medicinal%20root%20for%20immunity&width=300&height=200&seq=astragalus-herb&orientation=landscape'
      },
      {
        name: '桂皮（ケイヒ）',
        effect: '温陽化気、散寒止痛の効果',
        uses: ['冷え性', '腹痛', '関節痛'],
        image: 'https://readdy.ai/api/search-image?query=Cinnamon%20bark%20traditional%20Chinese%20medicine%20herb%2C%20dried%20brown%20medicinal%20bark%20for%20warming%20body&width=300&height=200&seq=cinnamon-herb&orientation=landscape'
      },
      {
        name: '半夏（ハンゲ）',
        effect: '痰を化し、嘔吐を止める',
        uses: ['吐き気', '咳痰', '胸のつかえ'],
        image: 'https://readdy.ai/api/search-image?query=Pinellia%20ternata%20tuber%20traditional%20Chinese%20medicine%20herb%2C%20dried%20white%20medicinal%20bulb%20for%20nausea&width=300&height=200&seq=pinellia-herb&orientation=landscape'
      },
      {
        name: '陳皮（チンピ）',
        effect: '気を理し、痰を化す',
        uses: ['消化不良', '咳痰', '胸のつかえ'],
        image: 'https://readdy.ai/api/search-image?query=Dried%20tangerine%20peel%20traditional%20Chinese%20medicine%20herb%2C%20orange%20citrus%20peel%20for%20digestion&width=300&height=200&seq=tangerine-peel-herb&orientation=landscape'
      },
      {
        name: '麻黄（マオウ）',
        effect: '発汗解表、宣肺平喘の効果',
        uses: ['風邪', '喘息', '浮腫'],
        image: 'https://readdy.ai/api/search-image?query=Ephedra%20sinica%20stem%20traditional%20Chinese%20medicine%20herb%2C%20dried%20green%20medicinal%20plant%20for%20respiratory%20health&width=300&height=200&seq=ephedra-herb&orientation=landscape'
      },
      {
        name: '杏仁（キョウニン）',
        effect: '咳を止め、腸を潤す',
        uses: ['咳嗽', '便秘', '喘息'],
        image: 'https://readdy.ai/api/search-image?query=Apricot%20kernel%20traditional%20Chinese%20medicine%20herb%2C%20dried%20medicinal%20seeds%20for%20cough%20relief&width=300&height=200&seq=apricot-kernel-herb&orientation=landscape'
      },
      {
        name: '石膏（セッコウ）',
        effect: '清熱瀉火、除煩止渇の効果',
        uses: ['高熱', '口渇', 'イライラ'],
        image: 'https://readdy.ai/api/search-image?query=Gypsum%20mineral%20traditional%20Chinese%20medicine%2C%20white%20crystalline%20mineral%20for%20fever%20reduction&width=300&height=200&seq=gypsum-herb&orientation=landscape'
      },
      {
        name: '知母（チモ）',
        effect: '清熱滋陰、潤燥滑腸の効果',
        uses: ['発熱', '口渇', '便秘'],
        image: 'https://readdy.ai/api/search-image?query=Anemarrhena%20asphodeloides%20rhizome%20traditional%20Chinese%20medicine%20herb%2C%20dried%20medicinal%20root%20for%20heat%20clearing&width=300&height=200&seq=anemarrhena-herb&orientation=landscape'
      },
      {
        name: '連翹（レンギョウ）',
        effect: '清熱解毒、散結消腫の効果',
        uses: ['感染症', '皮膚炎', 'リンパ腫脹'],
        image: 'https://readdy.ai/api/search-image?query=Forsythia%20suspensa%20fruit%20traditional%20Chinese%20medicine%20herb%2C%20dried%20yellow%20medicinal%20capsules%20for%20infection&width=300&height=200&seq=forsythia-herb&orientation=landscape'
      },
      {
        name: '金銀花（キンギンカ）',
        effect: '清熱解毒、疏散風熱の効果',
        uses: ['風邪', '皮膚感染', '咽頭炎'],
        image: 'https://readdy.ai/api/search-image?query=Honeysuckle%20flower%20traditional%20Chinese%20medicine%20herb%2C%20dried%20yellow%20white%20medicinal%20flowers%20for%20detox&width=300&height=200&seq=honeysuckle-herb&orientation=landscape'
      },
      {
        name: '桔梗（キキョウ）',
        effect: '宣肺祛痰、排膿の効果',
        uses: ['咳痰', '咽頭痛', '肺膿瘍'],
        image: 'https://readdy.ai/api/search-image?query=Platycodon%20grandiflorus%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20white%20medicinal%20root%20for%20lung%20health&width=300&height=200&seq=platycodon-herb&orientation=landscape'
      },
      {
        name: '枳実（キジツ）',
        effect: '破気消積、化痰除痞の効果',
        uses: ['胸腹脹満', '便秘', '痰飲'],
        image: 'https://readdy.ai/api/search-image?query=Citrus%20aurantium%20immature%20fruit%20traditional%20Chinese%20medicine%20herb%2C%20dried%20green%20medicinal%20citrus%20for%20digestion&width=300&height=200&seq=citrus-aurantium-herb&orientation=landscape'
      },
      {
        name: '厚朴（コウボク）',
        effect: '燥湿化痰、下気除満の効果',
        uses: ['腹部膨満', '下痢', '咳痰'],
        image: 'https://readdy.ai/api/search-image?query=Magnolia%20officinalis%20bark%20traditional%20Chinese%20medicine%20herb%2C%20dried%20brown%20medicinal%20bark%20for%20digestive%20health&width=300&height=200&seq=magnolia-bark-herb&orientation=landscape'
      },
      {
        name: '大黄（ダイオウ）',
        effect: '攻下通便、清熱解毒の効果',
        uses: ['便秘', '腹痛', '炎症'],
        image: 'https://readdy.ai/api/search-image?query=Rheum%20palmatum%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20yellow%20medicinal%20root%20for%20constipation&width=300&height=200&seq=rhubarb-herb&orientation=landscape'
      },
      {
        name: '芒硝（ボウショウ）',
        effect: '瀉下通便、清熱消腫の効果',
        uses: ['便秘', '腹水', '炎症'],
        image: 'https://readdy.ai/api/search-image?query=Sodium%20sulfate%20mineral%20traditional%20Chinese%20medicine%2C%20white%20crystalline%20mineral%20for%20laxative%20effect&width=300&height=200&seq=sodium-sulfate-herb&orientation=landscape'
      },
      {
        name: '柴胡（サイコ）',
        effect: '疏肝解鬱、升陽舉陥の効果',
        uses: ['ストレス', '発熱', '胃下垂'],
        image: 'https://readdy.ai/api/search-image?query=Bupleurum%20chinense%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20brown%20medicinal%20root%20for%20liver%20health&width=300&height=200&seq=bupleurum-herb&orientation=landscape'
      },
      {
        name: '黄連（オウレン）',
        effect: '清熱燥湿、瀉火解毒の効果',
        uses: ['下痢', '嘔吐', '口内炎'],
        image: 'https://readdy.ai/api/search-image?query=Coptis%20chinensis%20rhizome%20traditional%20Chinese%20medicine%20herb%2C%20dried%20yellow%20medicinal%20root%20for%20inflammation&width=300&height=200&seq=coptis-herb&orientation=landscape'
      },
      {
        name: '山梔子（サンシシ）',
        effect: '瀉火除煩、清熱利湿の効果',
        uses: ['イライラ', '黄疸', '尿路感染'],
        image: 'https://readdy.ai/api/search-image?query=Gardenia%20jasminoides%20fruit%20traditional%20Chinese%20medicine%20herb%2C%20dried%20red%20medicinal%20fruit%20for%20heat%20clearing&width=300&height=200&seq=gardenia-herb&orientation=landscape'
      },
      {
        name: '牡丹皮（ボタンピ）',
        effect: '清熱涼血、活血散瘀の効果',
        uses: ['皮膚炎', '月経不順', '打撲'],
        image: 'https://readdy.ai/api/search-image?query=Paeonia%20suffruticosa%20root%20bark%20traditional%20Chinese%20medicine%20herb%2C%20dried%20purple%20medicinal%20bark%20for%20blood%20circulation&width=300&height=200&seq=peony-bark-herb&orientation=landscape'
      },
      {
        name: '赤芍（セキシャク）',
        effect: '清熱涼血、散瘀止痛の効果',
        uses: ['炎症', '月経痛', '打撲痛'],
        image: 'https://readdy.ai/api/search-image?query=Red%20peony%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20reddish%20medicinal%20root%20for%20inflammation&width=300&height=200&seq=red-peony-herb&orientation=landscape'
      },
      {
        name: '丹参（タンジン）',
        effect: '活血祛瘀、涼血消癰の効果',
        uses: ['心疾患', '月経不順', '皮膚感染'],
        image: 'https://readdy.ai/api/search-image?query=Salvia%20miltiorrhiza%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20red%20medicinal%20root%20for%20heart%20health&width=300&height=200&seq=salvia-herb&orientation=landscape'
      },
      {
        name: '紅花（コウカ）',
        effect: '活血通経、散瘀止痛の効果',
        uses: ['月経不順', '血行不良', '打撲'],
        image: 'https://readdy.ai/api/search-image?query=Carthamus%20tinctorius%20flower%20traditional%20Chinese%20medicine%20herb%2C%20dried%20red%20medicinal%20flowers%20for%20blood%20circulation&width=300&height=200&seq=safflower-herb&orientation=landscape'
      },
      {
        name: '桃仁（トウニン）',
        effect: '活血祛瘀、潤腸通便の効果',
        uses: ['月経不順', '便秘', '打撲'],
        image: 'https://readdy.ai/api/search-image?query=Peach%20kernel%20traditional%20Chinese%20medicine%20herb%2C%20dried%20medicinal%20seeds%20for%20blood%20stasis&width=300&height=200&seq=peach-kernel-herb&orientation=landscape'
      },
      {
        name: '牛膝（ゴシツ）',
        effect: '補肝腎、強筋骨、活血通経の効果',
        uses: ['腰膝痛', '月経不順', '関節痛'],
        image: 'https://readdy.ai/api/search-image?query=Achyranthes%20bidentata%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20medicinal%20root%20for%20joint%20health&width=300&height=200&seq=achyranthes-herb&orientation=landscape'
      },
      {
        name: '威霊仙（イレイセン）',
        effect: '祛風湿、通経絡、消骨鯁の効果',
        uses: ['関節リウマチ', '神経痛', '魚骨刺傷'],
        image: 'https://readdy.ai/api/search-image?query=Clematis%20chinensis%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20medicinal%20root%20for%20rheumatism&width=300&height=200&seq=clematis-herb&orientation=landscape'
      },
      {
        name: '独活（ドッカツ）',
        effect: '祛風勝湿、散寒止痛の効果',
        uses: ['関節痛', '頭痛', '風湿痺痛'],
        image: 'https://readdy.ai/api/search-image?query=Angelica%20pubescens%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20medicinal%20root%20for%20joint%20pain&width=300&height=200&seq=angelica-pubescens-herb&orientation=landscape'
      },
      {
        name: '防風（ボウフウ）',
        effect: '祛風解表、勝湿止痛の効果',
        uses: ['風邪', '関節痛', '皮膚炎'],
        image: 'https://readdy.ai/api/search-image?query=Saposhnikovia%20divaricata%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20medicinal%20root%20for%20wind%20protection&width=300&height=200&seq=saposhnikovia-herb&orientation=landscape'
      },
      {
        name: '羌活（キョウカツ）',
        effect: '解表散寒、祛風勝湿の効果',
        uses: ['風邪', '頭痛', '関節痛'],
        image: 'https://readdy.ai/api/search-image?query=Notopterygium%20incisum%20rhizome%20traditional%20Chinese%20medicine%20herb%2C%20dried%20medicinal%20root%20for%20cold%20symptoms&width=300&height=200&seq=notopterygium-herb&orientation=landscape'
      },
      {
        name: '細辛（サイシン）',
        effect: '解表散寒、祛風止痛の効果',
        uses: ['風邪', '頭痛', '歯痛'],
        image: 'https://readdy.ai/api/search-image?query=Asarum%20sieboldii%20herb%20traditional%20Chinese%20medicine%2C%20dried%20small%20medicinal%20plant%20for%20pain%20relief&width=300&height=200&seq=asarum-herb&orientation=landscape'
      },
      {
        name: '白芷（ビャクシ）',
        effect: '解表散風、燥湿止帯の効果',
        uses: ['頭痛', '鼻炎', '帯下'],
        image: 'https://readdy.ai/api/search-image?query=Angelica%20dahurica%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20white%20medicinal%20root%20for%20headache&width=300&height=200&seq=angelica-dahurica-herb&orientation=landscape'
      },
      {
        name: '蒼朮（ソウジュツ）',
        effect: '燥湿健脾、祛風散寒の効果',
        uses: ['消化不良', 'むくみ', '関節痛'],
        image: 'https://readdy.ai/api/search-image?query=Atractylodes%20lancea%20rhizome%20traditional%20Chinese%20medicine%20herb%2C%20dried%20brown%20medicinal%20root%20for%20dampness&width=300&height=200&seq=atractylodes-lancea-herb&orientation=landscape'
      },
      {
        name: '薏苡仁（ヨクイニン）',
        effect: '利水滲湿、健脾止瀉の効果',
        uses: ['むくみ', 'いぼ', '下痢'],
        image: 'https://readdy.ai/api/search-image?query=Coix%20lacryma-jobi%20seeds%20traditional%20Chinese%20medicine%20herb%2C%20dried%20white%20medicinal%20seeds%20for%20water%20metabolism&width=300&height=200&seq=coix-herb&orientation=landscape'
      },
      {
        name: '沢瀉（タクシャ）',
        effect: '利水滲湿、瀉熱の効果',
        uses: ['むくみ', '尿路感染', '高血圧'],
        image: 'https://readdy.ai/api/search-image?query=Alisma%20orientale%20tuber%20traditional%20Chinese%20medicine%20herb%2C%20dried%20white%20medicinal%20bulb%20for%20water%20retention&width=300&height=200&seq=alisma-herb&orientation=landscape'
      },
      {
        name: '猪苓（チョレイ）',
        effect: '利水滲湿の効果',
        uses: ['むくみ', '尿路感染', '腎炎'],
        image: 'https://readdy.ai/api/search-image?query=Polyporus%20umbellatus%20mushroom%20traditional%20Chinese%20medicine%20herb%2C%20dried%20black%20medicinal%20fungus%20for%20urination&width=300&height=200&seq=polyporus-herb&orientation=landscape'
      },
      {
        name: '車前子（シャゼンシ）',
        effect: '利水通淋、滲湿止瀉の効果',
        uses: ['尿路感染', '下痢', '目の充血'],
        image: 'https://readdy.ai/api/search-image?query=Plantago%20asiatica%20seeds%20traditional%20Chinese%20medicine%20herb%2C%20dried%20small%20medicinal%20seeds%20for%20urinary%20health&width=300&height=200&seq=plantago-herb&orientation=landscape'
      },
      {
        name: '木通（モクツウ）',
        effect: '利水通淋、清心火の効果',
        uses: ['尿路感染', '心煩', '乳汁不通'],
        image: 'https://readdy.ai/api/search-image?query=Akebia%20quinata%20stem%20traditional%20Chinese%20medicine%20herb%2C%20dried%20brown%20medicinal%20vine%20for%20urinary%20tract&width=300&height=200&seq=akebia-herb&orientation=landscape'
      },
      {
        name: '滑石（カッセキ）',
        effect: '利水通淋、清熱解暑の効果',
        uses: ['尿路感染', '熱中症', '皮膚炎'],
        image: 'https://readdy.ai/api/search-image?query=Talc%20mineral%20traditional%20Chinese%20medicine%2C%20white%20smooth%20mineral%20powder%20for%20heat%20stroke&width=300&height=200&seq=talc-herb&orientation=landscape'
      },
      {
        name: '竜胆（リュウタン）',
        effect: '清熱燥湿、瀉肝胆火の効果',
        uses: ['肝炎', '胆嚢炎', '湿疹'],
        image: 'https://readdy.ai/api/search-image?query=Gentiana%20scabra%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20brown%20medicinal%20root%20for%20liver%20health&width=300&height=200&seq=gentiana-herb&orientation=landscape'
      },
      {
        name: '苦参（クジン）',
        effect: '清熱燥湿、殺虫利尿の効果',
        uses: ['皮膚炎', '湿疹', '寄生虫'],
        image: 'https://readdy.ai/api/search-image?query=Sophora%20flavescens%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20yellow%20medicinal%20root%20for%20skin%20conditions&width=300&height=200&seq=sophora-herb&orientation=landscape'
      },
      {
        name: '白鮮皮（ハクセンピ）',
        effect: '清熱燥湿、祛風解毒の効果',
        uses: ['湿疹', '皮膚炎', 'かゆみ'],
        image: 'https://readdy.ai/api/search-image?query=Dictamnus%20dasycarpus%20root%20bark%20traditional%20Chinese%20medicine%20herb%2C%20dried%20white%20medicinal%20bark%20for%20skin&width=300&height=200&seq=dictamnus-herb&orientation=landscape'
      },
      {
        name: '地膚子（ジフシ）',
        effect: '清熱利湿、祛風止痒の効果',
        uses: ['湿疹', 'かゆみ', '尿路感染'],
        image: 'https://readdy.ai/api/search-image?query=Kochia%20scoparia%20fruit%20traditional%20Chinese%20medicine%20herb%2C%20dried%20small%20medicinal%20seeds%20for%20itching&width=300&height=200&seq=kochia-herb&orientation=landscape'
      },
      {
        name: '蒲公英（ホコウエイ）',
        effect: '清熱解毒、利尿散結の効果',
        uses: ['感染症', '乳腺炎', '肝炎'],
        image: 'https://readdy.ai/api/search-image?query=Dandelion%20herb%20traditional%20Chinese%20medicine%2C%20dried%20medicinal%20plant%20for%20detoxification&width=300&height=200&seq=dandelion-herb&orientation=landscape'
      },
      {
        name: '紫花地丁（シカジチョウ）',
        effect: '清熱解毒、涼血消腫の効果',
        uses: ['皮膚感染', '乳腺炎', 'リンパ腫脹'],
        image: 'https://readdy.ai/api/search-image?query=Viola%20yedoensis%20herb%20traditional%20Chinese%20medicine%2C%20dried%20purple%20medicinal%20flowers%20for%20infection&width=300&height=200&seq=viola-herb&orientation=landscape'
      },
      {
        name: '板藍根（バンランコン）',
        effect: '清熱解毒、涼血利咽の効果',
        uses: ['風邪', 'インフルエンザ', '咽頭炎'],
        image: 'https://readdy.ai/api/search-image?query=Isatis%20tinctoria%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20blue%20medicinal%20root%20for%20viral%20infection&width=300&height=200&seq=isatis-herb&orientation=landscape'
      },
      {
        name: '大青葉（ダイセイヨウ）',
        effect: '清熱解毒、涼血消斑の効果',
        uses: ['発熱', '咽頭炎', '皮疹'],
        image: 'https://readdy.ai/api/search-image?query=Isatis%20tinctoria%20leaves%20traditional%20Chinese%20medicine%20herb%2C%20dried%20blue%20green%20medicinal%20leaves%20for%20fever&width=300&height=200&seq=isatis-leaves-herb&orientation=landscape'
      },
      {
        name: '青黛（セイタイ）',
        effect: '清熱解毒、涼血消斑の効果',
        uses: ['皮膚炎', '口内炎', '咳血'],
        image: 'https://readdy.ai/api/search-image?query=Indigo%20naturalis%20powder%20traditional%20Chinese%20medicine%2C%20blue%20medicinal%20powder%20for%20skin%20inflammation&width=300&height=200&seq=indigo-herb&orientation=landscape'
      },
      {
        name: '魚腥草（ギョセイソウ）',
        effect: '清熱解毒、消癰排膿の効果',
        uses: ['肺炎', '皮膚感染', '尿路感染'],
        image: 'https://readdy.ai/api/search-image?query=Houttuynia%20cordata%20herb%20traditional%20Chinese%20medicine%2C%20dried%20medicinal%20plant%20for%20lung%20infection&width=300&height=200&seq=houttuynia-herb&orientation=landscape'
      },
      {
        name: '金銭草（キンセンソウ）',
        effect: '利湿退黄、利尿通淋の効果',
        uses: ['胆石', '腎結石', '黄疸'],
        image: 'https://readdy.ai/api/search-image?query=Lysimachia%20christinae%20herb%20traditional%20Chinese%20medicine%2C%20dried%20round%20medicinal%20leaves%20for%20gallstones&width=300&height=200&seq=lysimachia-herb&orientation=landscape'
      },
      {
        name: '海金沙（カイキンシャ）',
        effect: '清熱解毒、利水通淋の効果',
        uses: ['尿路結石', '尿路感染', '血尿'],
        image: 'https://readdy.ai/api/search-image?query=Lygodium%20japonicum%20spores%20traditional%20Chinese%20medicine%20herb%2C%20golden%20medicinal%20powder%20for%20kidney%20stones&width=300&height=200&seq=lygodium-herb&orientation=landscape'
      },
      {
        name: '茵陳蒿（インチンコウ）',
        effect: '清湿熱、退黄疸の効果',
        uses: ['黄疸', '肝炎', '胆嚢炎'],
        image: 'https://readdy.ai/api/search-image?query=Artemisia%20scoparia%20herb%20traditional%20Chinese%20medicine%2C%20dried%20medicinal%20plant%20for%20jaundice&width=300&height=200&seq=artemisia-scoparia-herb&orientation=landscape'
      },
      {
        name: '梔子（シシ）',
        effect: '瀉火除煩、清熱利湿の効果',
        uses: ['イライラ', '不眠', '黄疸'],
        image: 'https://readdy.ai/api/search-image?query=Gardenia%20jasminoides%20fruit%20traditional%20Chinese%20medicine%20herb%2C%20dried%20red%20orange%20medicinal%20fruit%20for%20irritability&width=300&height=200&seq=gardenia-fruit-herb&orientation=landscape'
      },
      {
        name: '夏枯草（カゴソウ）',
        effect: '清肝火、散結腫の効果',
        uses: ['高血圧', 'リンパ腫脹', '甲状腺腫'],
        image: 'https://readdy.ai/api/search-image?query=Prunella%20vulgaris%20spike%20traditional%20Chinese%20medicine%20herb%2C%20dried%20brown%20medicinal%20flower%20spike%20for%20hypertension&width=300&height=200&seq=prunella-herb&orientation=landscape'
      },
      {
        name: '決明子（ケツメイシ）',
        effect: '清肝明目、潤腸通便の効果',
        uses: ['目の充血', '便秘', '高血圧'],
        image: 'https://readdy.ai/api/search-image?query=Cassia%20obtusifolia%20seeds%20traditional%20Chinese%20medicine%20herb%2C%20dried%20brown%20medicinal%20seeds%20for%20eye%20health&width=300&height=200&seq=cassia-herb&orientation=landscape'
      },
      {
        name: '車前草（シャゼンソウ）',
        effect: '清熱利尿、涼血解毒の効果',
        uses: ['尿路感染', '下痢', '咳痰'],
        image: 'https://readdy.ai/api/search-image?query=Plantago%20asiatica%20leaves%20traditional%20Chinese%20medicine%20herb%2C%20dried%20green%20medicinal%20leaves%20for%20urinary%20health&width=300&height=200&seq=plantago-leaves-herb&orientation=landscape'
      },
      {
        name: '馬歯莧（バシケン）',
        effect: '清熱解毒、涼血止血の効果',
        uses: ['下痢', '皮膚炎', '出血'],
        image: 'https://readdy.ai/api/search-image?query=Portulaca%20oleracea%20herb%20traditional%20Chinese%20medicine%2C%20dried%20succulent%20medicinal%20plant%20for%20diarrhea&width=300&height=200&seq=portulaca-herb&orientation=landscape'
      },
      {
        name: '白頭翁（ハクトウオウ）',
        effect: '清熱解毒、涼血止痢の効果',
        uses: ['赤痢', '下痢', '痔出血'],
        image: 'https://readdy.ai/api/search-image?query=Pulsatilla%20chinensis%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20white%20medicinal%20root%20for%20dysentery&width=300&height=200&seq=pulsatilla-herb&orientation=landscape'
      },
      {
        name: '秦皮（シンピ）',
        effect: '清熱燥湿、収斂止痢の効果',
        uses: ['下痢', '目の充血', '帯下'],
        image: 'https://readdy.ai/api/search-image?query=Fraxinus%20chinensis%20bark%20traditional%20Chinese%20medicine%20herb%2C%20dried%20brown%20medicinal%20bark%20for%20diarrhea&width=300&height=200&seq=fraxinus-herb&orientation=landscape'
      },
      {
        name: '椿皮（チンピ）',
        effect: '清熱燥湿、収斂止血の効果',
        uses: ['下痢', '痔出血', '帯下'],
        image: 'https://readdy.ai/api/search-image?query=Ailanthus%20altissima%20bark%20traditional%20Chinese%20medicine%20herb%2C%20dried%20medicinal%20bark%20for%20bleeding&width=300&height=200&seq=ailanthus-herb&orientation=landscape'
      },
      {
        name: '地榆（チユ）',
        effect: '涼血止血、解毒斂瘡の効果',
        uses: ['出血', '火傷', '湿疹'],
        image: 'https://readdy.ai/api/search-image?query=Sanguisorba%20officinalis%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20dark%20medicinal%20root%20for%20bleeding&width=300&height=200&seq=sanguisorba-herb&orientation=landscape'
      },
      {
        name: '槐花（カイカ）',
        effect: '涼血止血、清肝瀉火の効果',
        uses: ['痔出血', '高血圧', '頭痛'],
        image: 'https://readdy.ai/api/search-image?query=Sophora%20japonica%20flowers%20traditional%20Chinese%20medicine%20herb%2C%20dried%20yellow%20medicinal%20flowers%20for%20hemorrhoids&width=300&height=200&seq=sophora-flowers-herb&orientation=landscape'
      },
      {
        name: '側柏葉（ソクハクヨウ）',
        effect: '涼血止血、化痰止咳の効果',
        uses: ['出血', '脱毛', '咳痰'],
        image: 'https://readdy.ai/api/search-image?query=Platycladus%20orientalis%20leaves%20traditional%20Chinese%20medicine%20herb%2C%20dried%20green%20medicinal%20leaves%20for%20hair%20loss&width=300&height=200&seq=platycladus-herb&orientation=landscape'
      },
      {
        name: '白茅根（ハクボウコン）',
        effect: '涼血止血、清熱利尿の効果',
        uses: ['血尿', '鼻血', '熱中症'],
        image: 'https://readdy.ai/api/search-image?query=Imperata%20cylindrica%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20white%20medicinal%20root%20for%20nosebleed&width=300&height=200&seq=imperata-herb&orientation=landscape'
      },
      {
        name: '小薊（ショウケイ）',
        effect: '涼血止血、散瘀解毒の効果',
        uses: ['出血', '高血圧', '肝炎'],
        image: 'https://readdy.ai/api/search-image?query=Cirsium%20setosum%20herb%20traditional%20Chinese%20medicine%2C%20dried%20spiny%20medicinal%20plant%20for%20bleeding&width=300&height=200&seq=cirsium-herb&orientation=landscape'
      },
      {
        name: '大薊（ダイケイ）',
        effect: '涼血止血、散瘀消腫の効果',
        uses: ['出血', '打撲', '乳腺炎'],
        image: 'https://readdy.ai/api/search-image?query=Cirsium%20japonicum%20herb%20traditional%20Chinese%20medicine%2C%20dried%20large%20spiny%20medicinal%20plant%20for%20bleeding&width=300&height=200&seq=cirsium-japonicum-herb&orientation=landscape'
      },
      {
        name: '茜草（セイソウ）',
        effect: '涼血止血、活血祛瘀の効果',
        uses: ['月経過多', '出血', '関節痛'],
        image: 'https://readdy.ai/api/search-image?query=Rubia%20cordifolia%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20red%20medicinal%20root%20for%20menstrual%20bleeding&width=300&height=200&seq=rubia-herb&orientation=landscape'
      },
      {
        name: '蒲黄（ホオウ）',
        effect: '止血化瘀、利尿通淋の効果',
        uses: ['出血', '月経不順', '尿路感染'],
        image: 'https://readdy.ai/api/search-image?query=Typha%20angustifolia%20pollen%20traditional%20Chinese%20medicine%20herb%2C%20yellow%20medicinal%20powder%20for%20bleeding&width=300&height=200&seq=typha-herb&orientation=landscape'
      },
      {
        name: '三七（サンシチ）',
        effect: '止血散瘀、消腫定痛の効果',
        uses: ['外傷出血', '打撲', '心疾患'],
        image: 'https://readdy.ai/api/search-image?query=Panax%20notoginseng%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20brown%20medicinal%20root%20for%20trauma&width=300&height=200&seq=panax-notoginseng-herb&orientation=landscape'
      },
      {
        name: '白及（ビャクキュウ）',
        effect: '収斂止血、消腫生肌の効果',
        uses: ['胃出血', '外傷', '肺結核'],
        image: 'https://readdy.ai/api/search-image?query=Bletilla%20striata%20tuber%20traditional%20Chinese%20medicine%20herb%2C%20dried%20white%20medicinal%20bulb%20for%20wound%20healing&width=300&height=200&seq=bletilla-herb&orientation=landscape'
      },
      {
        name: '仙鶴草（センカクソウ）',
        effect: '収斂止血、截瘧止痢の効果',
        uses: ['下痢', '出血', 'マラリア'],
        image: 'https://readdy.ai/api/search-image?query=Agrimonia%20pilosa%20herb%20traditional%20Chinese%20medicine%2C%20dried%20medicinal%20plant%20with%20small%20yellow%20flowers%20for%20diarrhea&width=300&height=200&seq=agrimonia-herb&orientation=landscape'
      },
      {
        name: '棕櫚炭（シュロタン）',
        effect: '収斂止血の効果',
        uses: ['出血', '下血', '月経過多'],
        image: 'https://readdy.ai/api/search-image?query=Trachycarpus%20fortunei%20charcoal%20traditional%20Chinese%20medicine%2C%20black%20carbonized%20medicinal%20material%20for%20bleeding&width=300&height=200&seq=trachycarpus-herb&orientation=landscape'
      },
      {
        name: '血余炭（ケツヨタン）',
        effect: '収斂止血、化瘀利尿の効果',
        uses: ['出血', '血尿', '浮腫'],
        image: 'https://readdy.ai/api/search-image?query=Carbonized%20human%20hair%20traditional%20Chinese%20medicine%2C%20black%20medicinal%20charcoal%20for%20severe%20bleeding&width=300&height=200&seq=hair-charcoal-herb&orientation=landscape'
      },
      {
        name: '艾葉（ガイヨウ）',
        effect: '温経止血、散寒止痛の効果',
        uses: ['月経不順', '腹痛', '下痢'],
        image: 'https://readdy.ai/api/search-image?query=Artemisia%20argyi%20leaves%20traditional%20Chinese%20medicine%20herb%2C%20dried%20green%20medicinal%20leaves%20for%20menstrual%20disorders&width=300&height=200&seq=artemisia-argyi-herb&orientation=landscape'
      },
      {
        name: '炮姜（ホウキョウ）',
        effect: '温中止血、温経止痛の効果',
        uses: ['下血', '腹痛', '月経痛'],
        image: 'https://readdy.ai/api/search-image?query=Processed%20ginger%20traditional%20Chinese%20medicine%20herb%2C%20dried%20brown%20medicinal%20ginger%20for%20internal%20bleeding&width=300&height=200&seq=processed-ginger-herb&orientation=landscape'
      },
      {
        name: '灶心土（ソウシンド）',
        effect: '温中止血、止嘔止瀉の効果',
        uses: ['胃出血', '嘔吐', '下痢'],
        image: 'https://readdy.ai/api/search-image?query=Clay%20from%20old%20stove%20traditional%20Chinese%20medicine%2C%20yellow%20brown%20medicinal%20earth%20for%20stomach%20bleeding&width=300&height=200&seq=stove-clay-herb&orientation=landscape'
      },
      {
        name: '伏竜肝（フクリュウカン）',
        effect: '温中止血、止嘔安胎の効果',
        uses: ['胃出血', '嘔吐', '流産予防'],
        image: 'https://readdy.ai/api/search-image?query=Clay%20from%20kitchen%20stove%20traditional%20Chinese%20medicine%2C%20reddish%20medicinal%20earth%20for%20vomiting&width=300&height=200&seq=kitchen-clay-herb&orientation=landscape'
      },
      {
        name: '竹茹（チクジョ）',
        effect: '清熱化痰、除煩止嘔の効果',
        uses: ['嘔吐', '咳痰', 'イライラ'],
        image: 'https://readdy.ai/api/search-image?query=Bamboo%20shavings%20traditional%20Chinese%20medicine%20herb%2C%20dried%20green%20medicinal%20bamboo%20fiber%20for%20nausea&width=300&height=200&seq=bamboo-shavings-herb&orientation=landscape'
      },
      {
        name: '竹葉（チクヨウ）',
        effect: '清熱除煩、利尿の効果',
        uses: ['発熱', 'イライラ', '尿路感染'],
        image: 'https://readdy.ai/api/search-image?query=Bamboo%20leaves%20traditional%20Chinese%20medicine%20herb%2C%20dried%20green%20medicinal%20leaves%20for%20fever&width=300&height=200&seq=bamboo-leaves-herb&orientation=landscape'
      },
      {
        name: '淡竹葉（タンチクヨウ）',
        effect: '清熱除煩、利尿通淋の効果',
        uses: ['口渇', '尿路感染', '心煩'],
        image: 'https://readdy.ai/api/search-image?query=Lophatherum%20gracile%20leaves%20traditional%20Chinese%20medicine%20herb%2C%20dried%20thin%20medicinal%20grass%20for%20thirst&width=300&height=200&seq=lophatherum-herb&orientation=landscape'
      },
      {
        name: '蘆根（ロコン）',
        effect: '清熱生津、除煩止嘔の効果',
        uses: ['発熱', '口渇', '嘔吐'],
        image: 'https://readdy.ai/api/search-image?query=Phragmites%20australis%20rhizome%20traditional%20Chinese%20medicine%20herb%2C%20dried%20white%20medicinal%20root%20for%20thirst&width=300&height=200&seq=phragmites-herb&orientation=landscape'
      },
      {
        name: '天花粉（テンカフン）',
        effect: '清熱生津、消腫排膿の効果',
        uses: ['糖尿病', '口渇', '乳腺炎'],
        image: 'https://readdy.ai/api/search-image?query=Trichosanthes%20kirilowii%20root%20traditional%20Chinese%20medicine%20herb%2C%20dried%20white%20medicinal%20root%20for%20diabetes&width=300&height=200&seq=trichosanthes-herb&orientation=landscape'
      },
      {
        name: '麦門冬（バクモンドウ）',
        effect: '養陰生津、潤肺清心の効果',
        uses: ['発熱', '口渇', '便秘'],
        image: 'https://readdy.ai/api/search-image?query=Ophiopogon%20japonicus%20tuber%20traditional%20Chinese%20medicine%20herb%2C%20dried%20small%20medicinal%20bulbs%20for%20dry%20cough&width=300&height=200&seq=ophiopogon-herb&orientation=landscape'
      },
      {
        name: '石斛（セッコク）',
        effect: '養胃生津、滋陰清熱の効果',
        uses: ['胃炎', '口渇', '発熱'],
        image: 'https://readdy.ai/api/search-image?query=Dendrobium%20nobile%20stem%20traditional%20Chinese%20medicine%20herb%2C%20dried%20golden%20medicinal%20orchid%20stem%20for%20stomach&width=300&height=200&seq=dendrobium-herb&orientation=landscape'
      }
    ]
  };

  const consultationContent = {
    title: '漢方相談方法',
    description: '東洋医学の考え方に基づいた体質診断で、あなたに最適な漢方薬を考えます。',
    flowchart: {
      title: 'あなたの体質フローチャート',
      steps: [
        {
          id: 'start',
          question: '現在の主な症状は？',
          options: [
            { text: '疲れやすい・だるい', next: 'fatigue' },
            { text: '冷えが気になる', next: 'cold' },
            { text: 'のぼせ・ほてり', next: 'heat' },
            { text: 'イライラ・ストレス', next: 'stress' }
          ]
        },
        {
          id: 'fatigue',
          question: '疲労の特徴は？',
          options: [
            { text: '朝起きるのがつらい', next: 'qi_deficiency' },
            { text: '食後に眠くなる', next: 'spleen_qi_deficiency' },
            { text: '動悸・息切れがある', next: 'heart_qi_deficiency' },
            { text: '頭がぼーっとする', next: 'kidney_yang_deficiency' }
          ]
        },
        {
          id: 'cold',
          question: '冷えの部位は？',
          options: [
            { text: '手足の先が冷える', next: 'blood_deficiency' },
            { text: 'お腹が冷える', next: 'spleen_yang_deficiency' },
            { text: '腰や下半身が冷える', next: 'kidney_yang_deficiency' },
            { text: '全身が冷える', next: 'yang_deficiency' }
          ]
        },
        {
          id: 'heat',
          question: 'のぼせの特徴は？',
          options: [
            { text: '顎がほてる', next: 'yin_deficiency' },
            { text: '手足がほてる', next: 'yin_deficiency_heat' },
            { text: '寝汗をかく', next: 'yin_deficiency_night_sweat' },
            { text: '口が渇く', next: 'yin_deficiency_dryness' }
          ]
        },
        {
          id: 'stress',
          question: 'ストレスの症状は？',
          options: [
            { text: '胸がつかえる', next: 'qi_stagnation' },
            { text: 'お腹が張る', next: 'liver_qi_stagnation' },
            { text: '不眠・夢が多い', next: 'heart_blood_deficiency' },
            { text: '頭痛・肩こり', next: 'liver_yang_rising' }
          ]
        }
      ],
      results: {
        qi_deficiency: {
          type: '気虚体質',
          description: 'エネルギー不足で疲れやすい体質',
          symptoms: ['疲労感', '食欲不振', '息切れ', '風邪をひきやすい'],
          recommendations: ['補中益気湯', '十全大補湯', '人参養栄湯'],
          lifestyle: ['規則正しい生活', '適度な運動', '消化の良い食事'],
          color: 'bg-blue-50 text-blue-600'
        },
        spleen_qi_deficiency: {
          type: '脾気虚体質',
          description: '消化機能が弱く、栄養吸収が悪い体質',
          symptoms: ['食後の眠気', '軟便', '食欲不振', 'むくみ'],
          recommendations: ['六君子湯', '補中益気湯', '人参湯'],
          lifestyle: ['温かい食事', '規則的な食事時間', '冷たい物を控える'],
          color: 'bg-yellow-50 text-yellow-600'
        },
        heart_qi_deficiency: {
          type: '心気虚体質',
          description: '心臓の働きが弱く、循環が悪い体質',
          symptoms: ['動悸', '息切れ', '不安感', '不眠'],
          recommendations: ['炙甘草湯', '桂枝甘草湯', '安神定志丸'],
          lifestyle: ['ストレス管理', '適度な運動', '十分な睡眠'],
          color: 'bg-red-50 text-red-600'
        },
        blood_deficiency: {
          type: '血虚体質',
          description: '血液の栄養が不足している体質',
          symptoms: ['めまい', '立ちくらみ', '肌の乾燥', '不眠'],
          recommendations: ['当帰芍薬散', '四物湯', '婦人宝'],
          lifestyle: ['鉄分の多い食事', '十分な睡眠', '目の酷使を避ける'],
          color: 'bg-red-50 text-red-600'
        },
        spleen_yang_deficiency: {
          type: '脾陽虚体質',
          description: '消化機能が冷えて弱っている体質',
          symptoms: ['腹部の冷え', '下痢', '食欲不振', 'むくみ'],
          recommendations: ['理中湯', '真武湯', '附子理中湯'],
          lifestyle: ['温かい食事', '腹部を温める', '冷たい飲み物を控える'],
          color: 'bg-orange-50 text-orange-600'
        },
        kidney_yang_deficiency: {
          type: '腎陽虚体質',
          description: '体を温める力が不足している体質',
          symptoms: ['冷え性', '下痢しやすい', 'むくみ', '頻尿'],
          recommendations: ['真武湯', '附子理中湯', '八味地黄丸'],
          lifestyle: ['体を温める食事', '適度な運動', '十分な睡眠'],
          color: 'bg-purple-50 text-purple-600'
        },
        yang_deficiency: {
          type: '陽虚体質',
          description: '全身の陽気が不足している体質',
          symptoms: ['全身の冷え', '疲労感', '下痢', '浮腫'],
          recommendations: ['附子理中湯', '真武湯', '右帰丸'],
          lifestyle: ['温かい環境', '体を温める食事', '適度な運動'],
          color: 'bg-indigo-50 text-indigo-600'
        },
        yin_deficiency: {
          type: '陰虚体質',
          description: '体の潤いが不足している体質',
          symptoms: ['のぼせ', '手足のほてり', '口の渇き', '寝汗'],
          recommendations: ['知柏地黄丸', '麦門冬湯', '滋陰降火湯'],
          lifestyle: ['潤いのある食事', 'ストレス管理', '十分な水分補給'],
          color: 'bg-orange-50 text-orange-600'
        },
        yin_deficiency_heat: {
          type: '陰虚内熱体質',
          description: '陰液不足により内熱が生じている体質',
          symptoms: ['手足のほてり', '午後の微熱', '盗汗', '口渇'],
          recommendations: ['知柏地黄丸', '清骨散', '青蒿鼈甲湯'],
          lifestyle: ['涼性の食材', '夜更かしを避ける', '適度な水分補給'],
          color: 'bg-red-50 text-red-600'
        },
        yin_deficiency_night_sweat: {
          type: '陰虚盗汗体質',
          description: '陰液不足により寝汗をかきやすい体質',
          symptoms: ['寝汗', 'のぼせ', '不眠', '口渇'],
          recommendations: ['当帰六黄湯', '麦味地黄丸', '玉屏風散'],
          lifestyle: ['寝室の温度調整', '寝具の工夫', '夕食は軽めに'],
          color: 'bg-blue-50 text-blue-600'
        },
        yin_deficiency_dryness: {
          type: '陰虚燥熱体質',
          description: '陰液不足により乾燥症状が強い体質',
          symptoms: ['口渇', '皮膚乾燥', '便秘', 'のぼせ'],
          recommendations: ['麦門冬湯', '増液湯', '玄参清肺湯'],
          lifestyle: ['水分補給', '保湿ケア', '辛い物を控える'],
          color: 'bg-yellow-50 text-yellow-600'
        },
        qi_stagnation: {
          type: '気滞体質',
          description: '気の流れが滞りやすい体質',
          symptoms: ['胸のつかえ', 'ため息', '情緒不安定', '腹部膨満'],
          recommendations: ['半夏厚朴湯', '香蘇散', '逍遙散'],
          lifestyle: ['ストレス発散', '適度な運動', '規則的な生活'],
          color: 'bg-green-50 text-green-600'
        },
        liver_qi_stagnation: {
          type: '肝気鬱結体質',
          description: '肝の気が鬱滞している体質',
          symptoms: ['イライラ', '胸脇苦満', '腹部膨満', '月経不順'],
          recommendations: ['加味逍遙散', '柴胡疏肝散', '四逆散'],
          lifestyle: ['ストレス管理', '規則的な運動', '感情の発散'],
          color: 'bg-teal-50 text-teal-600'
        },
        heart_blood_deficiency: {
          type: '心血虚体質',
          description: '心の血が不足している体質',
          symptoms: ['不眠', '多夢', '健忘', '動悸'],
          recommendations: ['甘麦大棗湯', '酸棗仁湯', '帰脾湯'],
          lifestyle: ['規則的な睡眠', 'リラックス', '栄養バランス'],
          color: 'bg-pink-50 text-pink-600'
        },
        liver_yang_rising: {
          type: '肝陽上亢体質',
          description: '肝の陽気が上に昇りやすい体質',
          symptoms: ['頭痛', 'めまい', 'イライラ', '高血圧'],
          recommendations: ['釣藤散', '天麻鉤藤飲', '抑肝散'],
          lifestyle: ['ストレス管理', '塩分制限', '適度な運動'],
          color: 'bg-purple-50 text-purple-600'
        }
      }
    }
  };

  const getContentByCategory = (categoryId: string) => {
    switch (categoryId) {
      case 'constitution': return constitutionContent;
      case 'symptoms': return symptomsContent;
      case 'herbs': return herbsContent;
      case 'consultation': return consultationContent;
      default: return constitutionContent;
    }
  };

  const currentContent = getContentByCategory(activeCategory);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-10 h-10 mr-3 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-lg"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">サービス</Link>
              <Link to="/health-support" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
              <Link to="/pharmacist-resources" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">薬剤師向け</Link>
              <Link to="/information" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">情報提供</Link>
            </nav>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-50 to-orange-50 py-16">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Traditional%20Chinese%20medicine%20pharmacy%20with%20herbal%20medicines%2C%20wooden%20drawers%20with%20dried%20herbs%2C%20traditional%20kampo%20consultation%20room%2C%20natural%20healing%20environment%2C%20peaceful%20atmosphere&width=1200&height=400&seq=kampo-hero&orientation=landscape"
            alt="漢方相談サポート"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            漢方相談<span className="text-orange-600">サポート</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            東洋医学の知恵を活かし、あなたの体質に合った漢方薬をお選びします
          </p>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === category.id
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-orange-50 hover:text-orange-600'
                }`}
              >
                <i className={`${category.icon} mr-2`}></i>
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{currentContent.title}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{currentContent.description}</p>
          </div>

          {activeCategory === 'constitution' && (
            <div className="grid md:grid-cols-2 gap-8">
              {currentContent.types.map((type, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-8 border border-gray-100">
                  <div className={`w-12 h-12 rounded-lg ${type.color} flex items-center justify-center mb-6`}>
                    <i className="ri-user-line text-xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{type.name}</h3>
                  <p className="text-gray-600 mb-4">{type.description}</p>
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">主な症状:</h4>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {type.symptoms.map((symptom, idx) => (
                        <span key={idx} className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full">
                          {symptom}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">推奨漢方薬:</h4>
                    <div className="flex flex-wrap gap-1">
                      {type.recommendations.map((medicine, idx) => (
                        <span key={idx} className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">
                          {medicine}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeCategory === 'symptoms' && (
            <div className="grid md:grid-cols-2 gap-8">
              {currentContent.categories.map((category, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-8 border border-gray-100">
                  <div className="w-12 h-12 bg-orange-50 text-orange-600 rounded-lg flex items-center justify-center mb-6">
                    <i className={`${category.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{category.name}</h3>
                  <p className="text-gray-600 mb-4">{category.description}</p>
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">代表的な漢方薬:</h4>
                    <div className="flex flex-wrap gap-1">
                      {category.medicines.map((medicine, idx) => (
                        <span key={idx} className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                          {medicine}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <a 
                      href="https://rpx.a8.net/svt/ejp?a8mat=45BKFB+W5FG2+2HOM+6C1VM&rakuten=y&a8ejpredirect=http%3A%2F%2Fhb.afl.rakuten.co.jp%2Fhgc%2F0ea62065.34400275.0ea62066.204f04c0%2Fa25080201428_45BKFB_W5FG2_2HOM_6C1VM%3Fpc%3Dhttp%253A%252F%252Fwww.rakuten.co.jp%252F&m%3Dhttp%253A%252F%252Fm.rakuten.co.jp%2F"
                      rel="nofollow"
                      target="_blank"
                      className="bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      楽天で購入
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeCategory === 'herbs' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {currentContent.herbs.map((herb, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100 overflow-hidden">
                  <img 
                    src={herb.image} 
                    alt={herb.name}
                    className="w-full h-40 object-cover object-top"
                  />
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{herb.name}</h3>
                    <p className="text-gray-600 text-sm mb-3">{herb.effect}</p>
                    <div>
                      <h4 className="text-xs font-semibold text-gray-700 mb-1">主な用途:</h4>
                      <div className="flex flex-wrap gap-1">
                        {herb.uses.map((use, idx) => (
                          <span key={idx} className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                            {use}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeCategory === 'consultation' && (
            <div>
              {/* フローチャート診断 */}
              <div className="mb-16">
                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-8 mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">{currentContent.flowchart.title}</h3>
                  <p className="text-gray-600 text-center mb-8">以下の質問に答えて、あなたの体質を診断しましょう</p>
                  
                  <FlowchartDiagnosis flowchart={currentContent.flowchart} />
                </div>
              </div>

              {/* 相談の流れ */}
              <div className="grid md:grid-cols-4 gap-6">
                <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                  <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="font-bold">1</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">問診・体質診断</h3>
                  <p className="text-gray-600 text-sm">現在の症状や体質について詳しくお聞きします</p>
                </div>
                <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                  <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="font-bold">2</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">漢方薬の選択</h3>
                  <p className="text-gray-600 text-sm">体質と症状に最適な漢方薬をお選びします</p>
                </div>
                <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                  <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="font-bold">3</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">服用指導</h3>
                  <p className="text-gray-600 text-sm">正しい飲み方や注意点を詳しく説明します</p>
                </div>
                <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                  <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="font-bold">4</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">フォローアップ</h3>
                  <p className="text-gray-600 text-sm">効果を確認し、必要に応じて調整します</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Rakuten Affiliate Section */}
      <section className="py-16 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">漢方薬のお求めは</h2>
          <p className="text-lg text-gray-600 mb-8">
            信頼できる漢方薬を豊富な品揃えでご提供いたします
          </p>
          <div className="flex flex-col items-center gap-6">
            <a 
              href="https://rpx.a8.net/svt/ejp?a8mat=45BKFB+W5FG2+2HOM+68MF5&rakuten=y&a8ejpredirect=http%3A%2F%2Fhb.afl.rakuten.co.jp%2Fhgc%2F0ea62065.34400275.0ea62066.204f04c0%2Fa25080201428_45BKFB_W5FG2_2HOM_68MF5%3Fpc%3Dhttp%253A%252F%252Fwww.rakuten.co.jp%252F%26m%3Dhttp%253A%252F%252Fm.rakuten.co.jp%2F"
              rel="nofollow"
              target="_blank"
              className="cursor-pointer"
            >
              <img src="http://hbb.afl.rakuten.co.jp/hsb/0eb4bbc2.1a81da3f.0eb4bbaa.95151395/" border="0" alt="楽天バナー" />
            </a>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="https://rpx.a8.net/svt/ejp?a8mat=45BKFB+W5FG2+2HOM+6C1VM&rakuten=y&a8ejpredirect=http%3A%2F%2Fhb.afl.rakuten.co.jp%2Fhgc%2F0ea62065.34400275.0ea62066.204f04c0%2Fa25080201428_45BKFB_W5FG2_2HOM_6C1VM%3Fpc%3Dhttp%253A%252F%252Fwww.rakuten.co.jp%252F%26m%3Dhttp%253A%252F%252Fm.rakuten.co.jp%2F"
                rel="nofollow"
                target="_blank"
                className="bg-red-600 text-white px-12 py-4 rounded-full text-xl font-bold hover:bg-red-700 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
              >
                楽天で漢方薬を探す
              </a>
              <a 
                href="//ck.jp.ap.valuecommerce.com/servlet/referral?sid=3755218&pid=892142085"
                rel="nofollow"
                target="_blank"
                className="bg-blue-600 text-white px-12 py-4 rounded-full text-xl font-bold hover:bg-blue-700 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
              >
                Yahoo!ショッピングで探す
              </a>
            </div>
          </div>
          <img 
            width="1" 
            height="1" 
            src="https://www18.a8.net/0.gif?a8mat=45BKFB+W5FG2+2HOM+6C1VM" 
            alt=""
            className="hidden"
          />
          <img 
            border="0" 
            width="1" 
            height="1" 
            src="https://www13.a8.net/0.gif?a8mat=45BKFB+W5FG2+2HOM+68MF5" 
            alt=""
            className="hidden"
          />
          <img 
            src="//ad.jp.ap.valuecommerce.com/servlet/gifbanner?sid=3755218&pid=892142085" 
            height="1" 
            width="1" 
            border="0"
            alt=""
            className="hidden"
          />
        </div>
      </section>

      {/* Consultation Process */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">漢方相談の流れ</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="font-bold">1</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">問診・体質診断</h3>
              <p className="text-gray-600 text-sm">現在の症状や体質について詳しくお聞きします</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="font-bold">2</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">漢方薬の選択</h3>
              <p className="text-gray-600 text-sm">体質と症状に最適な漢方薬をお選びします</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="font-bold">3</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">服用指導</h3>
              <p className="text-gray-600 text-sm">正しい飲み方や注意点を詳しく説明します</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="font-bold">4</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">フォローアップ</h3>
              <p className="text-gray-600 text-sm">効果を確認し、必要に応じて調整します</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </div>
              <p className="text-gray-400">
                患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">患者さん向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/health-support" className="hover:text-white cursor-pointer">健康サポート</Link></li>
                <li><Link to="/self-care" className="hover:text-white cursor-pointer">セルフケア</Link></li>
                <li><Link to="/otc" className="hover:text-white cursor-pointer">OTC医薬品</Link></li>
                <li><Link to="/kampo" className="hover:text-white cursor-pointer">漢方相談</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">薬剤師向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/pharmacist-resources" className="hover:text-white cursor-pointer">専門リソース</Link></li>
                <li><Link to="/sterile-preparation" className="hover:text-white cursor-pointer">無菌調剤</Link></li>
                <li><Link to="/home-care" className="hover:text-white cursor-pointer">在宅医療</Link></li>
                <li><Link to="/dx" className="hover:text-white cursor-pointer">薬局DX</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/information" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                <li><Link to="/guidelines" className="hover:text-white cursor-pointer">疾患ガイドライン</Link></li>
                <li><Link to="/career" className="hover:text-white cursor-pointer">転職情報</Link></li>
                <li><Link to="/entertainment" className="hover:text-white cursor-pointer">エンタメ</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// フローチャート診断コンポーネント
function FlowchartDiagnosis({ flowchart }: { flowchart: any }) {
  const [currentStep, setCurrentStep] = useState('start');
  const [result, setResult] = useState<any>(null);
  const [history, setHistory] = useState<string[]>([]);

  const getCurrentStep = () => {
    return flowchart.steps.find((step: any) => step.id === currentStep);
  };

  const handleOptionClick = (nextStep: string) => {
    if (flowchart.results[nextStep]) {
      setResult(flowchart.results[nextStep]);
      setHistory([...history, currentStep]);
    } else {
      setCurrentStep(nextStep);
      setHistory([...history, currentStep]);
    }
  };

  const resetDiagnosis = () => {
    setCurrentStep('start');
    setResult(null);
    setHistory([]);
  };

  const goBack = () => {
    if (history.length > 0) {
      const previousStep = history[history.length - 1];
      setCurrentStep(previousStep);
      setHistory(history.slice(0, -1));
      setResult(null);
    }
  };

  if (result) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className={`rounded-xl p-8 ${result.color} border-2 border-opacity-20`}>
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-white bg-opacity-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-user-heart-line text-2xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-2">{result.type}</h3>
            <p className="text-lg opacity-80">{result.description}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white bg-opacity-50 rounded-lg p-4">
              <h4 className="font-semibold mb-3 flex items-center">
                <i className="ri-heart-pulse-line mr-2"></i>
                主な症状
              </h4>
              <div className="flex flex-wrap gap-2">
                {result.symptoms.map((symptom: string, idx: number) => (
                  <span key={idx} className="bg-white bg-opacity-70 px-3 py-1 rounded-full text-sm">
                    {symptom}
                  </span>
                ))}
              </div>
            </div>

            <div className="bg-white bg-opacity-50 rounded-lg p-4">
              <h4 className="font-semibold mb-3 flex items-center">
                <i className="ri-medicine-bottle-line mr-2"></i>
                推奨漢方薬
              </h4>
              <div className="flex flex-wrap gap-2">
                {result.recommendations.map((medicine: string, idx: number) => (
                  <span key={idx} className="bg-white bg-opacity-70 px-3 py-1 rounded-full text-sm font-medium">
                    {medicine}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white bg-opacity-50 rounded-lg p-4 mb-6">
            <h4 className="font-semibold mb-3 flex items-center">
              <i className="ri-lightbulb-line mr-2"></i>
              生活習慣のアドバイス
            </h4>
            <div className="flex flex-wrap gap-2">
              {result.lifestyle.map((advice: string, idx: number) => (
                <span key={idx} className="bg-white bg-opacity-70 px-3 py-1 rounded-full text-sm">
                  {advice}
                </span>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={resetDiagnosis}
              className="bg-white text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              もう一度診断する
            </button>
            <a 
              href="https://rpx.a8.net/svt/ejp?a8mat=45BKFB+W5FG2+2HOM+6C1VM&rakuten=y&a8ejpredirect=http%3A%2F%2Fhb.afl.rakuten.co.jp%2Fhgc%2F0ea62065.34400275.0ea62066.204f04c0%2Fa25080201428_45BKFB_W5FG2_2HOM_6C1VM%3Fpc%3Dhttp%253A%252F%252Fwww.rakuten.co.jp%252F&m%3Dhttp%253A%252F%252Fm.rakuten.co.jp%2F"
              rel="nofollow"
              target="_blank"
              className="bg-orange-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-orange-700 transition-colors text-center whitespace-nowrap cursor-pointer"
            >
              楽天で漢方薬を探す
            </a>
          </div>
        </div>
      </div>
    );
  }

  const step = getCurrentStep();
  if (!step) return null;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-question-line text-2xl"></i>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">{step.question}</h3>
          <p className="text-gray-600">当てはまるものを選択してください</p>
        </div>

        <div className="space-y-3 mb-8">
          {step.options.map((option: any, idx: number) => (
            <button
              key={idx}
              onClick={() => handleOptionClick(option.next)}
              className="w-full p-4 text-left bg-gray-50 hover:bg-orange-50 hover:border-orange-200 border border-gray-200 rounded-lg transition-all cursor-pointer"
            >
              <div className="flex items-center">
                <div className="w-6 h-6 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mr-3 text-sm font-bold">
                  {idx + 1}
                </div>
                <span className="font-medium">{option.text}</span>
              </div>
            </button>
          ))}
        </div>

        {history.length > 0 && (
          <div className="flex justify-center">
            <button
              onClick={goBack}
              className="flex items-center text-gray-600 hover:text-orange-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              前の質問に戻る
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
